import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Container,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import { Bell, GraduationCap, Home, LogOut, Menu as MenuIcon, Search, User, X } from 'lucide-react';

import TamanLogo from '../../assets/TamanLogo.svg?react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled';
import { useStore } from '../../pages/workspace/hooks.ts';
import { getInitials } from '../../services/util';
import { notificationsReducer } from '../../store/reducers/notifications.reducer.ts';
import { projectsReducer } from '../../store/reducers/projects.reducer.ts';
import { uiReducer } from '../../store/reducers/ui.reducer.ts';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getNotifications,
  getNotificationsLoaded,
  getUnseenNotificationsCount,
} from '../../store/selectors/notifications.selector';
import { getProfilePictureUrl, getSearchQuery, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { greyP4 } from '../../theme';
import { isPublic, requiresAuth } from '../../utils/authUtils';
import { NotificationsPopover } from '../notifications/NotificationsPopover';
import { AutosaveStatusIndicator } from '../workspace/AutosaveStatusIndicator';
import { WorkspaceVersionBar } from '../workspace/WorkspaceVersionBar';
import { NavbarProfile } from './NavbarProfile';

const { setSearchQuery, logout } = userReducer.actions;
const { updateProjectName } = projectsReducer.actions;
const { loadNotifications, markNotificationsRead } = notificationsReducer.actions;
const { setShowTutorials } = uiReducer.actions;

export const Navbar = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [editedProjectName, setEditedProjectName] = useState('');
  const [notifAnchorEl, setNotifAnchorEl] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { project } = useStore();

  const user = useAppSelector(getUser);
  const profilePictureUrl = useAppSelector(getProfilePictureUrl);
  const searchQuery = useAppSelector(getSearchQuery);
  const notificationsLoaded = useAppSelector(getNotificationsLoaded);
  const unseenCount = useAppSelector(getUnseenNotificationsCount);
  const notifications = useAppSelector(getNotifications);

  const userContext = requiresAuth(location.pathname);
  const isPublicPage = isPublic(location.pathname);
  const isWorkspacePage = location.pathname === '/workspace'; // Updated to exact match

  // With the "drawings" feature, projects (and drawings) have their search next to the section title.
  const drawingsEnabled = useDrawingsEnabled();
  const projectSearch = drawingsEnabled
    ? {}
    : {
        '/projects-new': { placeholder: t('navbar.search_project'), type: 'projects' },
        '/projects': { placeholder: t('navbar.search_project'), type: 'projects' },
      };
  const searchContexts = {
    ...projectSearch,
    '/users': { placeholder: t('navbar.search_user'), type: 'users' },
  };

  const searchContext =
    searchContexts[location.pathname as keyof typeof searchContexts] ||
    (location.pathname === '/' && !drawingsEnabled
      ? searchContexts['/projects' as keyof typeof searchContexts]
      : undefined);

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  useEffect(() => {
    dispatch(setSearchQuery(''));
  }, [location.pathname, dispatch]);

  // Update browser tab title with unread notifications count
  useEffect(() => {
    if (unseenCount > 0) {
      document.title = `${t('navbar.appName')} (${unseenCount})`;
    } else {
      document.title = t('navbar.appName');
    }
  }, [unseenCount, t]);

  useEffect(() => {
    if (user && !notificationsLoaded) {
      dispatch(loadNotifications());
    }
  }, [user, notificationsLoaded, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    history.push(path);
    setMobileMenuOpen(false);
  };

  const handleStartEditingProjectName = useCallback(() => {
    if (project?.name) {
      setEditedProjectName(project.name);
      setIsEditingProjectName(true);
    }
  }, [project?.name]);

  const handleCancelEditingProjectName = useCallback(() => {
    setIsEditingProjectName(false);
    setEditedProjectName('');
  }, []);

  const handleSaveProjectName = useCallback(() => {
    if (!project || !editedProjectName.trim() || editedProjectName === project.name) {
      handleCancelEditingProjectName();
      return;
    }

    dispatch(
      updateProjectName({
        projectId: project.id,
        name: editedProjectName.trim(),
      }),
    );

    setIsEditingProjectName(false);
    setEditedProjectName('');
  }, [project, editedProjectName, dispatch, handleCancelEditingProjectName]);

  const handleProjectNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSaveProjectName();
      } else if (e.key === 'Escape') {
        handleCancelEditingProjectName();
      }
    },
    [handleSaveProjectName, handleCancelEditingProjectName],
  );

  const mobileMenu = (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: greyP4,
          color: 'white',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ cursor: 'pointer' }} component={Link} to="/" onClick={() => setMobileMenuOpen(false)}>
          <TamanLogo />
        </Box>
        <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: 'white' }}>
          <X size={20} />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      {searchContext && !isWorkspacePage && (
        <Box sx={{ p: 2 }}>
          <Paper
            component="form"
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
            }}
          >
            <IconButton sx={{ p: '10px', color: 'rgba(255, 255, 255, 0.7)' }}>
              <Search size={18} />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, color: 'white' }}
              placeholder={searchContext.placeholder}
              value={searchQuery}
              onChange={handleSearchQueryChange}
              inputProps={{ 'aria-label': searchContext.placeholder }}
            />
          </Paper>
        </Box>
      )}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />
      {user && (
        <>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={profilePictureUrl}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: '#B3EC49',
                  color: 'black',
                  fontWeight: 'bold',
                  mr: 2,
                }}
              >
                {getInitials(user.fullName)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {user.fullName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <List>
            <ListItem component="div" onClick={() => handleNavigate('/profile')}>
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <User size={20} />
              </ListItemIcon>
              <ListItemText primary={t('navbar.profile')} />
            </ListItem>
            <ListItem component="div" onClick={handleLogout}>
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <LogOut size={20} />
              </ListItemIcon>
              <ListItemText primary={t('navbar.logout')} />
            </ListItem>
          </List>
        </>
      )}
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: greyP4,
          backgroundImage: 'none',
        }}
      >
        <Container sx={{ maxWidth: '100% !important', marginLeft: 'unset' }}>
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', height: 64, minHeight: 64 }}>
            {/* Mobile menu icon */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ display: { sm: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo on the left */}
            <Box
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              component={Link}
              to="/"
            >
              <TamanLogo />
            </Box>

            {/* Home Button */}
            {!isPublicPage && (
              <Box
                component={Link}
                to="/projects"
                sx={{
                  width: 64,
                  height: 64,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                  ml: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(179, 236, 73, 0.05)',
                  },
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                title={t('navbar.home')}
              >
                <Home size={24} style={{ color: '#B3EC49' }} />
              </Box>
            )}

            {/* Project Name - Only visible on Workspace page. Now sits in its
                own padded segment so logo / home / project name / right side
                all read as discrete cells of the top chrome. */}
            {isWorkspacePage && project && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: 64,
                  px: 3,
                  gap: 2,
                }}
              >
                {isEditingProjectName ? (
                  <TextField
                    value={editedProjectName}
                    onChange={(e) => setEditedProjectName(e.target.value)}
                    onKeyDown={handleProjectNameKeyDown}
                    onBlur={handleSaveProjectName}
                    autoFocus
                    size="small"
                    variant="standard"
                    sx={{
                      '& .MuiInput-root': {
                        color: '#B3EC49',
                        fontSize: '1.125rem',
                        fontWeight: 500,
                        '&:before': {
                          borderBottomColor: '#B3EC49',
                        },
                        '&:hover:before': {
                          borderBottomColor: '#B3EC49',
                        },
                        '&.Mui-focused:after': {
                          borderBottomColor: '#B3EC49',
                        },
                      },
                      '& .MuiInput-input': {
                        padding: '2px 0',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#B3EC49',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      cursor: 'pointer',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      transition: 'color 0.2s ease',
                      maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#c5f06b',
                      },
                    }}
                    onClick={handleStartEditingProjectName}
                    title={t('navbar.click_to_edit_project_name')}
                  >
                    {project.name}
                  </Typography>
                )}
              </Box>
            )}

            {/* Workspace-only: version status pill + split action block. Sits
                immediately to the right of the project name (~20px gap) so
                the "project → version" relationship reads left-to-right. The
                action block keeps a right border to mark the segment edge
                before the open spacer area begins. */}
            {isWorkspacePage && project && (
              <Box sx={{ display: 'flex', alignSelf: 'stretch', ml: '20px' }}>
                <WorkspaceVersionBar />
                <AutosaveStatusIndicator />
              </Box>
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Right side with search, notifications, and profile.
                Padded segment with explicit gap; the version bar (workspace
                only) renders as the next segment after this one. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2 }}>
              {/* Search Bar - Only show on Projects page */}
              {searchContext && !isWorkspacePage && (
                <Paper
                  component="form"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    width: { sm: '220px', md: '250px' },
                    bgcolor: '#111111',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  }}
                >
                  <IconButton sx={{ p: '10px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    <Search size={18} />
                  </IconButton>
                  <InputBase
                    sx={{ ml: 1, flex: 1, color: 'white' }}
                    placeholder={searchContext.placeholder}
                    value={searchQuery}
                    onChange={handleSearchQueryChange}
                    inputProps={{ 'aria-label': searchContext.placeholder }}
                  />
                </Paper>
              )}

              {/* Notifications Bell */}
              {user && (
                <Tooltip
                  title={
                    unseenCount > 0
                      ? t('notifications.newNotifications', { count: unseenCount })
                      : t('notifications.title')
                  }
                >
                  <IconButton
                    onClick={(e) => {
                      setNotifAnchorEl(e.currentTarget);
                      dispatch(markNotificationsRead(undefined));
                    }}
                    sx={{ color: 'white' }}
                    aria-label="notifications"
                    aria-haspopup="true"
                    aria-expanded={notifAnchorEl ? 'true' : undefined}
                  >
                    <Badge badgeContent={unseenCount} color="error" overlap="circular" invisible={!unseenCount}>
                      <Bell size={20} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              {/* Tutorials */}
              {user && (
                <Tooltip title={t('navbar.tutorials')}>
                  <IconButton
                    onClick={() => dispatch(setShowTutorials(true))}
                    sx={{ color: 'white' }}
                    aria-label="tutorials"
                  >
                    <GraduationCap size={20} />
                  </IconButton>
                </Tooltip>
              )}

              {/* Profile Avatar */}
              <NavbarProfile userContext={userContext} />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile menu drawer */}
      {mobileMenu}

      {/* Notifications Popover */}
      <NotificationsPopover
        open={Boolean(notifAnchorEl)}
        anchorEl={notifAnchorEl}
        onClose={() => setNotifAnchorEl(null)}
        notifications={notifications}
      />
    </>
  );
};
